import { lazy, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';

/**
 * Pages that stay mounted after their first visit so their state
 * (search filters, fetched results, pagination, scroll) survives
 * navigation: hidden via `display: none` rather than unmounted.
 * Rendered once from Layout (always mounted), not per-route.
 * DataManagement is intentionally not here — it must remount so a new
 * deep link renders a different restaurant.
 */
const Dashboard = lazy(() => import('../pages/Dashboard'));
const Restaurants = lazy(() => import('../pages/Restaurants'));
const BusinessIntelligence = lazy(() => import('../pages/BusinessIntelligence'));
const Notifications = lazy(() => import('../pages/Notifications'));
const Users = lazy(() => import('../pages/Users'));
const DataOnboarding = lazy(() => import('../pages/DataOnboarding'));
const Tools = lazy(() => import('../pages/Tools'));

const PERSISTENT_PAGES = [
  { path: '/', component: Dashboard },
  { path: '/bi', component: BusinessIntelligence },
  { path: '/restaurants', component: Restaurants },
  { path: '/notifications', component: Notifications },
  { path: '/users', component: Users },
  { path: '/onboarding', component: DataOnboarding },
  { path: '/tools', component: Tools },
];

function KeepAlivePage({ active, children }) {
  // Mount on first visit (or when already active at startup); keep
  // mounted afterwards, toggling only visibility.
  const [visited, setVisited] = useState(active);
  const savedScroll = useRef(0);
  useEffect(() => {
    if (active) {
      setVisited(true);
      if (savedScroll.current) {
        // Restore where the user was when this page was hidden.
        const offset = savedScroll.current;
        savedScroll.current = 0;
        const t = setTimeout(() => window.scrollTo({ top: offset }), 50);
        return () => clearTimeout(t);
      }
      return undefined;
    }
    // Capture the current scroll position before hiding this page.
    savedScroll.current = window.scrollY;
    return undefined;
  }, [active]);
  if (!visited) return null;
  return <div style={{ display: active ? 'block' : 'none' }}>{children}</div>;
}

export default function PersistentPages() {
  const location = useLocation();
  const { can } = usePermissions();
  const allowed = {
    '/users': can('viewUsers'),
    '/restaurants': can('viewRestaurants'),
    '/notifications': can('viewNotifications'),
    '/onboarding': can('onboarding'),
    '/tools': can('storageHygiene') || can('onboarding') || can('imageSyncTool'),
  };
  return (
    <>
      {PERSISTENT_PAGES.map(({ path, component: Page }) => (
        allowed[path] === false ? null : (
          <KeepAlivePage key={path} active={location.pathname === path}>
            <Page />
          </KeepAlivePage>
        )
      ))}
    </>
  );
}
